/**
 * useCompletion Hook
 * 
 * Fetches store-level completion data for an operator.
 * Used by Route Overview to display completion percentages.
 */

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

export interface StoreCompletion {
  storeId: number;
  assignmentId: number | null;
  totalTasks: number;
  completedTasks: number;
  completionPercentage: number;
  postingComplete: boolean; // All work/tasks done
  isSigned: boolean; // Has manager signature
  isComplete: boolean; // ONLY true if postingComplete && isSigned
}

export function useCompletion(cycleId: number | string | undefined) {
  return useQuery<StoreCompletion[]>({
    // Keyed by 'completion' so ManagerSignatureModal invalidations using ['completion'] apply correctly
    queryKey: ['completion', cycleId],
    queryFn: async () => {
      if (!cycleId) {
        throw new Error('Cycle ID required');
      }

      const response = await apiFetch(`/api/mobile/completion/${cycleId}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch completion data: ${response.status}`);
      }

      const data = await response.json();
      return data as StoreCompletion[];
    },
    enabled: !!cycleId,
    staleTime: 30_000, // 30 seconds (matches cache header)
    refetchInterval: 30_000, // Auto-refetch every 30 seconds
    retry: 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

